import { useState } from 'react';
import { Check, Loader2, X } from 'lucide-react';
import { useSubscription } from '../../contexts/SubscriptionContext';
import { redirectToCheckout } from '../../services/stripeService';
import analytics from '../../services/analyticsService';
import { useCopy, fill } from '../../i18n';
import { plural } from '../../i18n/format';
import { cn } from '@/lib/utils';
import { showToast } from '../common/Toast';
import { Dialog, DialogContent } from '../ui/dialog';
import { Button } from '../ui/button';
import type { Plan } from '../../types';

interface PricingPageProps {
  onClose: () => void;
}

/**
 * O que cada plano mostra na lista de features: a ORDEM e o `included` (com ou
 * sem check) são regra de produto e ficam aqui; o `id` indexa o texto em
 * `account.billing.pricing.features`. A chave do Record é o nome do plano no
 * banco ('free', 'starter', 'professional'), então não se traduz.
 */
const PLAN_FEATURES: Record<string, { id: string; included: boolean }[]> = {
  free: [
    { id: 'projects3', included: true },
    { id: 'ganttBasic', included: true },
    { id: 'kanban', included: true },
    { id: 'calendar', included: true },
    { id: 'exportCsvLimited', included: true },
    { id: 'templates', included: false },
    { id: 'baseline', included: false },
    { id: 'organizations', included: false },
    { id: 'dependencies', included: false },
    { id: 'raci', included: false },
    { id: 'wbs', included: false },
    { id: 'criticalPath', included: false },
    { id: 'portfolio', included: false },
    { id: 'risksChanges', included: false },
    { id: 'costs', included: false },
  ],
  starter: [
    { id: 'projects10', included: true },
    { id: 'ganttDependencies', included: true },
    { id: 'kanbanSprints', included: true },
    { id: 'calendarTimeline', included: true },
    { id: 'templates', included: true },
    { id: 'baseline', included: true },
    { id: 'organizations10', included: true },
    { id: 'todos', included: true },
    { id: 'raci', included: true },
    { id: 'wbs', included: true },
    { id: 'exportCsvUnlimited', included: true },
    { id: 'sharePublic', included: true },
    { id: 'criticalPath', included: false },
    { id: 'portfolio', included: false },
    { id: 'risksChanges', included: false },
    { id: 'capacity', included: false },
  ],
  professional: [
    { id: 'projectsUnlimited', included: true },
    { id: 'allFeatures', included: true },
    { id: 'organizationsUnlimited', included: true },
    { id: 'templates', included: true },
    { id: 'baseline', included: true },
    { id: 'criticalPath', included: true },
    { id: 'portfolio', included: true },
    { id: 'capacity', included: true },
    { id: 'risksChanges', included: true },
    { id: 'costs', included: true },
    { id: 'exportPdfPng', included: true },
    { id: 'sharePassword', included: true },
    { id: 'apiAccess', included: true },
  ],
};

export default function PricingPage({ onClose }: PricingPageProps) {
  const { plans, planName, formatPrice, isActive } = useSubscription();
  const t = useCopy().account.billing.pricing;
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [loadingPlan, setLoadingPlan] = useState<string | null>(null);

  // Detectar trial via query param (?trial=30)
  const trialDays = (() => {
    const params = new URLSearchParams(window.location.search);
    const val = parseInt(params.get('trial') || '', 10);
    return val >= 1 && val <= 90 ? val : null;
  })();

  const handleSubscribe = async (plan: Plan) => {
    if (plan.name === 'free') return;
    if (plan.name === planName && isActive) return;

    setLoadingPlan(plan.name);
    try {
      const priceId = billingCycle === 'yearly'
        ? plan.stripe_price_id_yearly
        : plan.stripe_price_id_monthly;

      if (!priceId) {
        analytics.trackCheckoutError('stripe_unconfigured', plan.name);
        showToast(t.stripeUnconfigured, 'error');
        return;
      }

      // price está em centavos no banco; GA4 espera o valor em reais.
      const price = billingCycle === 'yearly' ? plan.price_yearly : plan.price_monthly;
      analytics.trackCheckoutInitiated(plan.name, billingCycle, price / 100);
      await redirectToCheckout(priceId, billingCycle, trialDays ?? undefined);
    } catch (error) {
      console.error('Erro ao iniciar checkout:', error);
      analytics.trackCheckoutError('checkout_failed', plan.name);
      showToast(t.checkoutError, 'error');
    } finally {
      setLoadingPlan(null);
    }
  };

  const getButtonText = (plan: Plan) => {
    // `plan.name` é o identificador do banco e do Stripe: compara, não exibe.
    if (plan.name === planName && isActive) {
      return t.currentPlan;
    }
    if (plan.name === 'free') {
      return t.startFree;
    }
    if (trialDays && plan.name !== 'free') {
      return fill(plural(trialDays, t.trialCta), { count: trialDays });
    }
    return t.subscribe;
  };

  const getPrice = (plan: Plan) => {
    if (plan.price_monthly === 0) return t.free;
    const price = billingCycle === 'yearly' ? plan.price_yearly / 12 : plan.price_monthly;
    return formatPrice(price);
  };

  const getSavings = (plan: Plan) => {
    if (plan.price_monthly === 0) return null;
    const yearlyMonthly = plan.price_yearly / 12;
    const savings = ((plan.price_monthly - yearlyMonthly) / plan.price_monthly) * 100;
    return Math.round(savings);
  };

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent className="max-w-[1100px] p-10 gap-0 max-h-[90vh] overflow-y-auto">
        <div className="text-center mb-10">
          <h1 className="text-[32px] font-bold text-[var(--color-text)] mb-2">{t.title}</h1>
          <p className="text-[var(--color-text-secondary)] text-base">{t.subtitle}</p>

          <div className="inline-flex bg-[var(--color-bg-tertiary)] rounded-[var(--radius-lg)] p-1 mt-6">
            <Button
              variant="ghost"
              className={cn("py-2.5 px-6 border-none bg-none text-[var(--color-text-secondary)] text-sm font-medium rounded-[var(--radius-md)] transition-all duration-200 flex items-center gap-2 hover:translate-y-0 hover:shadow-none", billingCycle === 'monthly' && "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary)] hover:text-white")}
              onClick={() => setBillingCycle('monthly')}
            >
              {t.monthly}
            </Button>
            <Button
              variant="ghost"
              className={cn("py-2.5 px-6 border-none bg-none text-[var(--color-text-secondary)] text-sm font-medium rounded-[var(--radius-md)] transition-all duration-200 flex items-center gap-2 hover:translate-y-0 hover:shadow-none", billingCycle === 'yearly' && "bg-[var(--color-primary)] text-white hover:bg-[var(--color-primary)] hover:text-white")}
              onClick={() => setBillingCycle('yearly')}
            >
              {t.yearly}
              <span className="bg-[var(--color-primary)] text-white text-[11px] py-0.5 px-1.5 rounded-[var(--radius-sm)] font-semibold">{t.discountBadge}</span>
            </Button>
          </div>
        </div>

        <div className="grid grid-cols-3 gap-6 mb-10 max-lg:grid-cols-1">
          {plans.map((plan) => {
            const savings = getSavings(plan);
            return (
            <div
              key={plan.id}
              className={cn("bg-[var(--color-bg)] rounded-[var(--radius-xl)] p-8 relative border border-[var(--color-border)] transition-all duration-200 hover:border-[var(--color-border-dark)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.08)]", plan.name === 'professional' && "border-2 border-[var(--color-primary)] scale-[1.02]", plan.name === planName && isActive && plan.name !== 'professional' && "border-2 border-[var(--color-primary)]")}
            >
              {plan.name === 'professional' && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-[var(--color-primary)] text-white py-1 px-4 rounded-[20px] text-xs font-semibold">{t.mostPopular}</div>
              )}

              {/* Nome comercial e descrição do plano vêm do banco (`plans`) e
                  não passam pelo dicionário. */}
              <div className="mb-6">
                <h2 className="text-2xl font-bold text-[var(--color-text)] mb-2">{plan.display_name}</h2>
                <p className="text-[var(--color-text-secondary)] text-sm leading-snug">{plan.description}</p>
              </div>

              <div className="mb-6">
                <span className="text-4xl font-bold text-[var(--color-text)]">{getPrice(plan)}</span>
                {plan.price_monthly > 0 && (
                  <span className="text-[var(--color-text-secondary)] text-sm ml-1">{t.perUserMonth}</span>
                )}
                {billingCycle === 'yearly' && savings !== null && savings > 0 && (
                  <span className="block text-[var(--color-primary)] text-[13px] font-medium mt-1">{fill(t.savings, { pct: savings })}</span>
                )}
              </div>

              <Button
                variant="primary"
                size="xl"
                className={cn("w-full rounded-[var(--radius-lg)]", plan.name === planName && isActive && "cursor-default opacity-80")}
                onClick={() => handleSubscribe(plan)}
                disabled={loadingPlan === plan.name || (plan.name === planName && isActive)}
              >
                {loadingPlan === plan.name ? (
                  <Loader2 size={18} className="animate-spin" />
                ) : (
                  getButtonText(plan)
                )}
              </Button>

              <ul className="list-none p-0 mt-6">
                {PLAN_FEATURES[plan.name]?.map((feature) => (
                  <li key={feature.id} className={cn("flex items-center gap-2.5 py-2 text-sm", feature.included ? "text-[var(--color-text)]" : "text-[var(--color-text-muted)]")}>
                    {feature.included ? (
                      <Check size={16} className="text-[var(--color-primary)] shrink-0" />
                    ) : (
                      <X size={16} className="text-[var(--color-border-dark)] shrink-0" />
                    )}
                    {t.features[feature.id as keyof typeof t.features]}
                  </li>
                ))}
              </ul>
            </div>
            );
          })}
        </div>

        <div className="text-center pt-6 border-t border-[var(--color-border)]">
          <p className="text-[var(--color-text-secondary)] text-[13px] my-1">{t.footerSupport}</p>
          <p className="text-[var(--color-text-secondary)] text-[13px] my-1">{t.footerContact}</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
